import { Injectable } from '@angular/core';

/**
 * Client-side cryptographic operations for DEK unlock flow.
 *
 * Uses the Web Crypto API exclusively — no third-party dependencies.
 * The user's private key password never leaves the browser.
 *
 * Flow:
 *   1. Derive AES-256 key from password via scrypt (N=16384, r=8, p=1)
 *   2. Decrypt RSA-4096 private key with AES-256-GCM
 *   3. Unwrap per-dossier DEK with RSA-OAEP (SHA-256)
 */
@Injectable({ providedIn: 'root' })
export class CryptoService {

  /** Cached RSA private key — lives for the browser tab session. */
  private cachedPrivateKey: CryptoKey | null = null;
  /** Cached scrypt-derived AES key material. */
  private cachedDerivedKey: ArrayBuffer | null = null;
  /** Salt used for the cached derived key (to detect same-password calls). */
  private cachedSaltHex: string | null = null;
  /** Set of dossier IDs successfully unlocked in this session. */
  private unlockedDossierIds = new Set<string>();

  /**
   * Derive a 256-bit AES key from a password using scrypt.
   *
   * Parameters match the backend (dd_keypair):
   *   N = 2^14 = 16384, r = 8, p = 1, dkLen = 32
   *
   * Web Crypto API does not natively support scrypt, so we implement
   * the core algorithm using PBKDF2 as the HMAC primitive would be
   * incorrect. Instead we use a pure JS scrypt implementation that
   * operates on ArrayBuffers for performance.
   */
  async deriveKeyFromPassword(password: string, saltHex: string, bypassCache = false): Promise<ArrayBuffer> {
    // Return cached derived key if salt matches (same password + salt).
    // bypassCache skips both read and write — used when verifying a password
    // the caller will validate via decryption (wrong password must not pollute cache).
    if (!bypassCache && this.cachedDerivedKey && this.cachedSaltHex === saltHex) {
      return this.cachedDerivedKey;
    }

    const encoder = new TextEncoder();
    const passwordBytes = encoder.encode(password);
    const salt = this.hexToBytes(saltHex);

    // scrypt parameters matching backend (dd_keypair)
    const N = 16384; // 2^14
    const r = 8;
    const p = 1;
    const dkLen = 32;

    const derived = await this.scrypt(passwordBytes, salt, N, r, p, dkLen);

    if (!bypassCache) {
      // Cache for subsequent calls with the same salt
      this.cachedDerivedKey = derived;
      this.cachedSaltHex = saltHex;
    }

    return derived;
  }

  /**
   * Decrypt an RSA private key (PEM, PKCS8) encrypted with AES-256-GCM.
   *
   * @param derivedKey  32-byte AES key from scrypt
   * @param ciphertextHex  Hex-encoded AES-256-GCM ciphertext (encrypted private key PEM)
   * @param nonceHex  Hex-encoded 12-byte GCM nonce
   * @returns  CryptoKey (RSA private key) importable for RSA-OAEP operations
   */
  async decryptPrivateKey(
    derivedKey: ArrayBuffer,
    ciphertextHex: string,
    nonceHex: string,
  ): Promise<CryptoKey> {
    const ciphertext = this.hexToBytes(ciphertextHex);
    const nonce = this.hexToBytes(nonceHex);

    const aesKey = await crypto.subtle.importKey(
      'raw', derivedKey, { name: 'AES-GCM' }, false, ['decrypt'],
    );

    let decrypted: ArrayBuffer;
    try {
      decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: nonce },
        aesKey,
        ciphertext,
      );
    } catch {
      // Wrong password — evict cached derived key so a retry with the correct
      // password is not blocked by the stale cache entry.
      this.cachedDerivedKey = null;
      this.cachedSaltHex = null;
      throw new Error('wrongPassword');
    }

    // The decrypted data is the RSA private key in PEM format (PKCS8)
    const pemString = new TextDecoder().decode(decrypted);
    const der = this.pemToDer(pemString);

    return crypto.subtle.importKey(
      'pkcs8', der,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false, ['decrypt'],
    ).then(privateKey => {
      // Cache the decrypted private key for session-level reuse
      this.cachedPrivateKey = privateKey;
      return privateKey;
    });
  }

  /**
   * Unwrap (decrypt) a per-dossier DEK using RSA-OAEP.
   *
   * @param privateKey  RSA CryptoKey from decryptPrivateKey()
   * @param encryptedDekHex  Hex-encoded RSA-OAEP ciphertext of the 32-byte DEK
   * @returns  Hex-encoded cleartext DEK (64 hex chars)
   */
  async unwrapDek(privateKey: CryptoKey, encryptedDekHex: string): Promise<string> {
    const encryptedDek = this.hexToBytes(encryptedDekHex);

    const dekBytes = await crypto.subtle.decrypt(
      { name: 'RSA-OAEP' },
      privateKey,
      encryptedDek,
    );

    return this.bytesToHex(new Uint8Array(dekBytes));
  }

  // --- Session-level caching API ---

  /** Return the cached RSA private key, or null if not yet unlocked. */
  getCachedPrivateKey(): CryptoKey | null {
    return this.cachedPrivateKey;
  }

  /** True when a cached private key is available (password entered this session). */
  hasCachedPrivateKey(): boolean {
    return this.cachedPrivateKey !== null;
  }

  /** Wipe all cached key material and unlocked dossier tracking. Call on logout. */
  clearCache(): void {
    this.cachedPrivateKey = null;
    this.cachedDerivedKey = null;
    this.cachedSaltHex = null;
    this.unlockedDossierIds.clear();
  }

  /** Record a dossier as successfully unlocked in this session. */
  addUnlockedDossier(id: string): void {
    this.unlockedDossierIds.add(id);
  }

  /** Check whether a dossier has been unlocked in this session. */
  isDossierUnlocked(id: string): boolean {
    return this.unlockedDossierIds.has(id);
  }

  /** Remove a dossier from the unlocked set (e.g. when the DEK turns out to be stale). */
  removeUnlockedDossier(id: string): void {
    this.unlockedDossierIds.delete(id);
  }

  // ---------------------------------------------------------------------------
  // Key generation, re-encryption, DEK re-wrap, and P12 export
  // ---------------------------------------------------------------------------

  /** Generate a new RSA-4096 keypair in the browser. Returns PEM strings. */
  async generateKeypairBrowser(): Promise<{ publicKeyPem: string; privateKeyPem: string }> {
    const keypair = await crypto.subtle.generateKey(
      {
        name: 'RSA-OAEP',
        modulusLength: 4096,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256',
      },
      true,
      ['encrypt', 'decrypt'],
    );
    const pubDer  = new Uint8Array(await crypto.subtle.exportKey('spki',  keypair.publicKey));
    const privDer = new Uint8Array(await crypto.subtle.exportKey('pkcs8', keypair.privateKey));
    return {
      publicKeyPem:  this.derToPem(pubDer,  'PUBLIC KEY'),
      privateKeyPem: this.derToPem(privDer, 'PRIVATE KEY'),
    };
  }

  /**
   * Encrypt a private key PEM for storage using scrypt + AES-256-GCM.
   * Mirrors decryptPrivateKey(). Generates fresh random salt and nonce each call.
   */
  async encryptPrivateKeyForStorage(
    privateKeyPem: string,
    password: string,
  ): Promise<{ encrypted_private_key: string; private_key_salt: string; private_key_nonce: string }> {
    const salt  = crypto.getRandomValues(new Uint8Array(32));
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const saltHex  = this.bytesToHex(salt);
    const nonceHex = this.bytesToHex(nonce);

    const derivedKey = await this.deriveKeyFromPassword(password, saltHex);
    const aesKey  = await crypto.subtle.importKey('raw', derivedKey, { name: 'AES-GCM' }, false, ['encrypt']);
    const plaintext  = new TextEncoder().encode(privateKeyPem);
    const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, aesKey, plaintext));

    return {
      encrypted_private_key: this.bytesToHex(ciphertext),
      private_key_salt:  saltHex,
      private_key_nonce: nonceHex,
    };
  }

  /**
   * Decrypt an encrypted private key and return the raw PEM string.
   * Used when the plaintext PEM is needed (P12 export, re-encryption for
   * password change).  For DEK unlock use decryptPrivateKey() instead.
   */
  async decryptPrivateKeyToPem(
    derivedKey: ArrayBuffer,
    ciphertextHex: string,
    nonceHex: string,
  ): Promise<string> {
    const ciphertext = this.hexToBytes(ciphertextHex);
    const nonce      = this.hexToBytes(nonceHex);
    const aesKey     = await crypto.subtle.importKey('raw', derivedKey, { name: 'AES-GCM' }, false, ['decrypt']);
    try {
      const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce }, aesKey, ciphertext);
      return new TextDecoder().decode(decrypted);
    } catch {
      this.cachedDerivedKey = null;
      this.cachedSaltHex = null;
      throw new Error('wrongPassword');
    }
  }

  /**
   * Wrap (encrypt) a DEK with a recipient's RSA-4096 public key (PEM/SPKI).
   * Used during key rotation to re-wrap retiring-slot DEKs for the new slot.
   *
   * @param dekHex        Cleartext DEK as a hex string (from unwrapDek)
   * @param publicKeyPem  Recipient public key in PEM/SPKI format
   * @returns             Hex-encoded RSA-OAEP ciphertext
   */
  async wrapDekWithPublicKey(dekHex: string, publicKeyPem: string): Promise<string> {
    const der = this.pemToDer(publicKeyPem);
    const publicKey = await crypto.subtle.importKey(
      'spki', der,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false, ['encrypt'],
    );
    const dek       = this.hexToBytes(dekHex);
    const encrypted = new Uint8Array(await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, dek));
    return this.bytesToHex(encrypted);
  }

  /**
   * Build a PKCS#12 (.p12) file containing the private key.
   *
   * Structure:
   *   PFX v3
   *   └─ authSafe (pkcs7-data)
   *      └─ PKCS#8ShroudedKeyBag
   *         └─ EncryptedPrivateKeyInfo (PBES2: PBKDF2-HMAC-SHA256 + AES-256-CBC)
   *   └─ MacData (HMAC-SHA-1, key via PKCS#12 KDF — RFC 7292 Appendix B)
   *
   * @param privateKeyPem  Cleartext RSA private key in PKCS#8 PEM format
   * @param exportPassword Password for the .p12 file (independent of login password)
   * @returns              DER-encoded PFX bytes ready for download
   */
  async buildPkcs12(privateKeyPem: string, exportPassword: string): Promise<Uint8Array> {
    const passwordBytes = new TextEncoder().encode(exportPassword);
    const privKeyDer    = new Uint8Array(this.pemToDer(privateKeyPem));
    const kdfIterations = 2048;

    // --- PBES2: PBKDF2-HMAC-SHA256 + AES-256-CBC ---
    const kdfSalt = crypto.getRandomValues(new Uint8Array(16));
    const cbcIv   = crypto.getRandomValues(new Uint8Array(16));

    const encBaseKey = await crypto.subtle.importKey('raw', passwordBytes, 'PBKDF2', false, ['deriveBits']);
    const encKeyBits = await crypto.subtle.deriveBits(
      { name: 'PBKDF2', salt: kdfSalt, iterations: kdfIterations, hash: 'SHA-256' },
      encBaseKey, 256,
    );
    const aesKey = await crypto.subtle.importKey('raw', encKeyBits, { name: 'AES-CBC' }, false, ['encrypt']);
    const encPrivKeyBytes = new Uint8Array(
      await crypto.subtle.encrypt({ name: 'AES-CBC', iv: cbcIv }, aesKey, privKeyDer),
    );

    // --- Build DER ---
    const oidPkcs7Data        = this.derOid('1.2.840.113549.1.7.1');
    const oidShroudedKeyBag   = this.derOid('1.2.840.113549.1.12.10.1.2');
    const oidPbes2            = this.derOid('1.2.840.113549.1.5.13');
    const oidPbkdf2           = this.derOid('1.2.840.113549.1.5.12');
    const oidHmacSha256       = this.derOid('1.2.840.113549.2.9');
    const oidAes256Cbc        = this.derOid('2.16.840.1.101.3.4.1.42');

    // PBES2 AlgorithmIdentifier
    const hmacSha256AlgId = this.derSeq(this.concat(oidHmacSha256, this.derNull()));
    const pbkdf2Params    = this.derSeq(this.concat(
      this.derOctetString(kdfSalt),
      this.derIntegerN(kdfIterations),
      this.derIntegerN(32),    // keyLength
      hmacSha256AlgId,
    ));
    const pbkdf2AlgId  = this.derSeq(this.concat(oidPbkdf2, pbkdf2Params));
    const aesCbcAlgId  = this.derSeq(this.concat(oidAes256Cbc, this.derOctetString(cbcIv)));
    const pbes2Params  = this.derSeq(this.concat(pbkdf2AlgId, aesCbcAlgId));
    const encAlgId     = this.derSeq(this.concat(oidPbes2, pbes2Params));

    // EncryptedPrivateKeyInfo → SafeBag → SafeContents → ContentInfo
    const encPrivKeyInfo = this.derSeq(this.concat(encAlgId, this.derOctetString(encPrivKeyBytes)));
    const safeBag        = this.derSeq(this.concat(
      oidShroudedKeyBag,
      this.derContextExplicit(0, encPrivKeyInfo),
    ));
    const safeContents   = this.derSeq(safeBag);
    const bagContentInfo = this.derSeq(this.concat(
      oidPkcs7Data,
      this.derContextExplicit(0, this.derOctetString(safeContents)),
    ));

    // authSafe outer ContentInfo
    const authSafeContent = this.derSeq(bagContentInfo);
    const authSafe        = this.derSeq(this.concat(
      oidPkcs7Data,
      this.derContextExplicit(0, this.derOctetString(authSafeContent)),
    ));

    // --- MAC: HMAC-SHA-1, key via PKCS#12 KDF (RFC 7292 Appendix B, id=3) ---
    const macSalt       = crypto.getRandomValues(new Uint8Array(20));
    const macIterations = 2048;
    const passwordBmp   = this.toBmpString(exportPassword);

    const macKeyBytes = await this.pkcs12KDF(passwordBmp, macSalt, macIterations, 3, 20);
    const hmacKey  = await crypto.subtle.importKey('raw', macKeyBytes, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
    const macBytes = new Uint8Array(await crypto.subtle.sign('HMAC', hmacKey, authSafeContent));

    const oidSha1     = this.derOid('1.3.14.3.2.26');
    const sha1AlgId   = this.derSeq(this.concat(oidSha1, this.derNull()));
    const digestInfo  = this.derSeq(this.concat(sha1AlgId, this.derOctetString(macBytes)));
    const macData     = this.derSeq(this.concat(
      digestInfo,
      this.derOctetString(macSalt),
      this.derIntegerN(macIterations),
    ));

    // PFX
    return this.derSeq(this.concat(this.derIntegerN(3), authSafe, macData));
  }

  // --- Hex conversion utilities ---

  private hexToBytes(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
    }
    return bytes;
  }

  private bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // --- PEM to DER conversion ---

  private pemToDer(pem: string): ArrayBuffer {
    const lines = pem.split('\n').filter(l => !l.startsWith('-----'));
    const b64 = lines.join('');
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // ---------------------------------------------------------------------------
  // Pure JS scrypt implementation (RFC 7914)
  //
  // No third-party dependencies. Uses Web Crypto for PBKDF2-HMAC-SHA256
  // as the underlying PRF for the initial and final key derivation steps.
  // The core scrypt mixing (Salsa20/8, BlockMix, ROMix) is pure arithmetic.
  // ---------------------------------------------------------------------------

  private async scrypt(
    password: Uint8Array, salt: Uint8Array,
    N: number, r: number, p: number, dkLen: number,
  ): Promise<ArrayBuffer> {
    // Step 1: B = PBKDF2-HMAC-SHA256(password, salt, 1, p * 128 * r)
    const B = new Uint8Array(await this.pbkdf2Sha256(password, salt, 1, p * 128 * r));

    // Step 2: ROMix each block
    for (let i = 0; i < p; i++) {
      const offset = i * 128 * r;
      const block = B.subarray(offset, offset + 128 * r);
      this.romix(block, r, N);
    }

    // Step 3: DK = PBKDF2-HMAC-SHA256(password, B, 1, dkLen)
    return this.pbkdf2Sha256(password, B, 1, dkLen);
  }

  private async pbkdf2Sha256(
    password: Uint8Array, salt: Uint8Array, iterations: number, dkLen: number,
  ): Promise<ArrayBuffer> {
    const baseKey = await crypto.subtle.importKey(
      'raw', password, 'PBKDF2', false, ['deriveBits'],
    );
    return crypto.subtle.deriveBits(
      { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' },
      baseKey, dkLen * 8,
    );
  }

  /**
   * ROMix: the memory-hard core of scrypt.
   * Operates in-place on the block (128*r bytes).
   */
  private romix(block: Uint8Array, r: number, N: number): void {
    const blockLen = 128 * r;
    const blockWords = blockLen / 4;

    // Convert block to uint32 array (little-endian)
    const X = new Uint32Array(blockWords);
    const dv = new DataView(block.buffer, block.byteOffset, block.byteLength);
    for (let i = 0; i < blockWords; i++) {
      X[i] = dv.getUint32(i * 4, true);
    }

    // V[i] = X after i iterations of BlockMix
    const V = new Uint32Array(N * blockWords);
    for (let i = 0; i < N; i++) {
      V.set(X, i * blockWords);
      this.blockMix(X, r);
    }

    // Mix phase
    for (let i = 0; i < N; i++) {
      // j = Integerify(X) mod N
      const j = X[blockWords - 16] & (N - 1); // N is power of 2
      for (let k = 0; k < blockWords; k++) {
        X[k] ^= V[j * blockWords + k];
      }
      this.blockMix(X, r);
    }

    // Write back to block (little-endian)
    for (let i = 0; i < blockWords; i++) {
      dv.setUint32(i * 4, X[i], true);
    }
  }

  /**
   * scrypt BlockMix using Salsa20/8 as the core hash.
   * Operates in-place on X (2*r * 16 uint32 words).
   */
  private blockMix(X: Uint32Array, r: number): void {
    const numBlocks = 2 * r;
    const T = new Uint32Array(16);
    const Y = new Uint32Array(numBlocks * 16);

    // T = X[2r-1] (last 64-byte block)
    T.set(X.subarray((numBlocks - 1) * 16, numBlocks * 16));

    for (let i = 0; i < numBlocks; i++) {
      // T = T XOR X[i]
      for (let j = 0; j < 16; j++) {
        T[j] ^= X[i * 16 + j];
      }
      // T = Salsa20/8(T)
      this.salsa208(T);
      // Y[i] = T
      Y.set(T, i * 16);
    }

    // Rearrange: even blocks first, then odd blocks
    for (let i = 0; i < r; i++) {
      X.set(Y.subarray(2 * i * 16, (2 * i + 1) * 16), i * 16);
    }
    for (let i = 0; i < r; i++) {
      X.set(Y.subarray((2 * i + 1) * 16, (2 * i + 2) * 16), (r + i) * 16);
    }
  }

  /**
   * Salsa20/8 core (8 rounds of Salsa20).
   * Operates in-place on a 16-word (64-byte) block.
   */
  private salsa208(B: Uint32Array): void {
    const x = new Uint32Array(16);
    x.set(B);

    for (let i = 0; i < 8; i += 2) {
      // Column round
      x[ 4] ^= this.rotl32(x[ 0] + x[12], 7);
      x[ 8] ^= this.rotl32(x[ 4] + x[ 0], 9);
      x[12] ^= this.rotl32(x[ 8] + x[ 4], 13);
      x[ 0] ^= this.rotl32(x[12] + x[ 8], 18);
      x[ 9] ^= this.rotl32(x[ 5] + x[ 1], 7);
      x[13] ^= this.rotl32(x[ 9] + x[ 5], 9);
      x[ 1] ^= this.rotl32(x[13] + x[ 9], 13);
      x[ 5] ^= this.rotl32(x[ 1] + x[13], 18);
      x[14] ^= this.rotl32(x[10] + x[ 6], 7);
      x[ 2] ^= this.rotl32(x[14] + x[10], 9);
      x[ 6] ^= this.rotl32(x[ 2] + x[14], 13);
      x[10] ^= this.rotl32(x[ 6] + x[ 2], 18);
      x[ 3] ^= this.rotl32(x[15] + x[11], 7);
      x[ 7] ^= this.rotl32(x[ 3] + x[15], 9);
      x[11] ^= this.rotl32(x[ 7] + x[ 3], 13);
      x[15] ^= this.rotl32(x[11] + x[ 7], 18);
      // Row round
      x[ 1] ^= this.rotl32(x[ 0] + x[ 3], 7);
      x[ 2] ^= this.rotl32(x[ 1] + x[ 0], 9);
      x[ 3] ^= this.rotl32(x[ 2] + x[ 1], 13);
      x[ 0] ^= this.rotl32(x[ 3] + x[ 2], 18);
      x[ 6] ^= this.rotl32(x[ 5] + x[ 4], 7);
      x[ 7] ^= this.rotl32(x[ 6] + x[ 5], 9);
      x[ 4] ^= this.rotl32(x[ 7] + x[ 6], 13);
      x[ 5] ^= this.rotl32(x[ 4] + x[ 7], 18);
      x[11] ^= this.rotl32(x[10] + x[ 9], 7);
      x[ 8] ^= this.rotl32(x[11] + x[10], 9);
      x[ 9] ^= this.rotl32(x[ 8] + x[11], 13);
      x[10] ^= this.rotl32(x[ 9] + x[ 8], 18);
      x[12] ^= this.rotl32(x[15] + x[14], 7);
      x[13] ^= this.rotl32(x[12] + x[15], 9);
      x[14] ^= this.rotl32(x[13] + x[12], 13);
      x[15] ^= this.rotl32(x[14] + x[13], 18);
    }

    for (let i = 0; i < 16; i++) {
      B[i] = (B[i] + x[i]) >>> 0;
    }
  }

  private rotl32(v: number, bits: number): number {
    return ((v << bits) | (v >>> (32 - bits))) >>> 0;
  }

  // ---------------------------------------------------------------------------
  // PKCS#12 password encoding & KDF (RFC 7292 Appendix B)
  // ---------------------------------------------------------------------------

  /**
   * Encode a password as BMPString (UTF-16BE) with a trailing 0x0000
   * terminator, as required by the PKCS#12 KDF.
   */
  private toBmpString(str: string): Uint8Array {
    const result = new Uint8Array(str.length * 2 + 2);
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i);
      result[i * 2]     = (code >> 8) & 0xff;
      result[i * 2 + 1] = code & 0xff;
    }
    // trailing 0x0000 is already zero from Uint8Array init
    return result;
  }

  /**
   * PKCS#12 KDF (RFC 7292 Appendix B) using SHA-1.
   *
   * @param passwordBmp  Password encoded as BMPString (toBmpString output)
   * @param salt         Random salt bytes
   * @param iterations   Iteration count
   * @param id           Diversifier: 1 = encryption key, 2 = IV, 3 = MAC key
   * @param keyLen       Desired output length in bytes
   */
  private async pkcs12KDF(
    passwordBmp: Uint8Array, salt: Uint8Array,
    iterations: number, id: number, keyLen: number,
  ): Promise<Uint8Array> {
    const u = 20;  // SHA-1 output length
    const v = 64;  // SHA-1/SHA-256 block size

    // D = diversifier: v bytes all set to id
    const D = new Uint8Array(v).fill(id);

    // S = salt repeated to fill ceil(salt.length / v) * v bytes
    const sLen = salt.length > 0 ? v * Math.ceil(salt.length / v) : 0;
    const S = new Uint8Array(sLen);
    for (let i = 0; i < sLen; i++) S[i] = salt[i % salt.length];

    // P = password repeated to fill ceil(password.length / v) * v bytes
    const pLen = passwordBmp.length > 0 ? v * Math.ceil(passwordBmp.length / v) : 0;
    const P = new Uint8Array(pLen);
    for (let i = 0; i < pLen; i++) P[i] = passwordBmp[i % passwordBmp.length];

    // I = S || P (mutable — updated in-place between rounds)
    const I = new Uint8Array(sLen + pLen);
    I.set(S, 0);
    I.set(P, sLen);

    const result = new Uint8Array(keyLen);
    let offset = 0;

    while (offset < keyLen) {
      // A = H^iterations(D || I)
      const DI = new Uint8Array(v + I.length);
      DI.set(D, 0);
      DI.set(I, v);
      let A = new Uint8Array(await crypto.subtle.digest('SHA-1', DI));
      for (let iter = 1; iter < iterations; iter++) {
        A = new Uint8Array(await crypto.subtle.digest('SHA-1', A));
      }

      const toCopy = Math.min(u, keyLen - offset);
      result.set(A.subarray(0, toCopy), offset);
      offset += toCopy;

      if (offset < keyLen) {
        // B = A repeated to v bytes
        const B = new Uint8Array(v);
        for (let i = 0; i < v; i++) B[i] = A[i % u];
        // I_j = (I_j + B + 1) mod 2^(v*8) for each v-byte block
        for (let j = 0; j < I.length; j += v) {
          let carry = 1;
          for (let k = v - 1; k >= 0; k--) {
            const sum = I[j + k] + B[k] + carry;
            I[j + k] = sum & 0xff;
            carry = sum >> 8;
          }
        }
      }
    }

    return result;
  }

  // ---------------------------------------------------------------------------
  // DER / PEM encoding helpers (used by buildPkcs12 and generateKeypairBrowser)
  // ---------------------------------------------------------------------------

  private derToPem(der: Uint8Array, label: string): string {
    let binary = '';
    for (let i = 0; i < der.length; i++) {
      binary += String.fromCharCode(der[i]);
    }
    const b64   = btoa(binary);
    const lines = b64.match(/.{1,64}/g)!.join('\n');
    return `-----BEGIN ${label}-----\n${lines}\n-----END ${label}-----`;
  }

  private concat(...arrays: Uint8Array[]): Uint8Array {
    const total = arrays.reduce((s, a) => s + a.length, 0);
    const out   = new Uint8Array(total);
    let off = 0;
    for (const a of arrays) { out.set(a, off); off += a.length; }
    return out;
  }

  private derLength(len: number): Uint8Array {
    if (len < 0x80)   return new Uint8Array([len]);
    if (len < 0x100)  return new Uint8Array([0x81, len]);
    return new Uint8Array([0x82, (len >> 8) & 0xff, len & 0xff]);
  }

  private derTLV(tag: number, value: Uint8Array): Uint8Array {
    return this.concat(new Uint8Array([tag]), this.derLength(value.length), value);
  }

  private derSeq(content: Uint8Array): Uint8Array { return this.derTLV(0x30, content); }

  private derOctetString(data: Uint8Array): Uint8Array { return this.derTLV(0x04, data); }

  private derNull(): Uint8Array { return new Uint8Array([0x05, 0x00]); }

  private derContextExplicit(tag: number, content: Uint8Array): Uint8Array {
    return this.derTLV(0xa0 | tag, content);
  }

  /** Encode a non-negative integer (up to 24-bit) as DER INTEGER. */
  private derIntegerN(n: number): Uint8Array {
    if (n <= 0x7f)     return this.derTLV(0x02, new Uint8Array([n]));
    if (n <= 0x7fff)   return this.derTLV(0x02, new Uint8Array([(n >> 8) & 0xff, n & 0xff]));
    return this.derTLV(0x02, new Uint8Array([(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]));
  }

  /** Encode a dotted OID string as DER OID. */
  private derOid(dotted: string): Uint8Array {
    const parts = dotted.split('.').map(Number);
    const bytes: number[] = [40 * parts[0] + parts[1]];
    for (let i = 2; i < parts.length; i++) {
      let val = parts[i];
      const encoded: number[] = [val & 0x7f];
      val >>= 7;
      while (val > 0) { encoded.unshift((val & 0x7f) | 0x80); val >>= 7; }
      bytes.push(...encoded);
    }
    return this.derTLV(0x06, new Uint8Array(bytes));
  }
}
